// Auto-recovers from stale chunk errors after a deploy, and forwards any other
// unhandled errors/rejections to Datadog so they don't silently disappear.
//
// When a new build is deployed, users with the previous build's index.html cached
// in their browser hold references to chunk filenames (e.g. Sidebar-a1b2c3.js) that
// no longer exist on the CDN. Triggering a lazy import that needs an old chunk
// 404s and throws "Failed to fetch dynamically imported module" / ChunkLoadError,
// leaving the user on a broken page until they manually refresh.
//
// We listen for those errors globally and force a one-time reload to fetch the new
// index.html with the new chunk hashes. A sessionStorage timestamp guards against
// reload loops if the new build is also broken.
//
// Everything else (real uncaught exceptions and rejected promises that don't flow
// through ErrorNotification) is forwarded to datadogRum.addError so it counts as
// a real error in monitoring.

import { datadogRum } from '@datadog/browser-rum';

const RELOAD_FLAG_KEY = 'chunkErrorReloadAt';
const RELOAD_DEDUPE_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'Loading CSS chunk',
  'error loading dynamically imported module',
];

export const isChunkError = (message: unknown): boolean => {
  if (!message || typeof message !== 'string') return false;
  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

const hasRecentlyReloaded = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG_KEY));
    return Boolean(last) && Date.now() - last < RELOAD_DEDUPE_MS;
  } catch {
    return false;
  }
};

const markReloaded = () => {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {}
};

// Returns `true` if a reload was triggered, `false` if suppressed by the
// dedupe guard. Callers that have a fallback path (e.g. throwing the error
// up to an error boundary) can branch on the result.
const recover = (): boolean => {
  if (hasRecentlyReloaded()) return false;
  markReloaded();
  window.location.reload();
  return true;
};

// Public entry for callers that have already classified the error (e.g. the
// `loadable` wrapper catching a rejected `import()`). Same dedupe guard so
// repeated triggers within RELOAD_DEDUPE_MS won't loop.
export const recoverFromChunkError = recover;

const toError = (reason: unknown): Error => {
  if (reason instanceof Error) return reason;
  return new Error(typeof reason === 'string' ? reason : 'Unhandled rejection');
};

export const setupChunkErrorRecovery = () => {
  window.addEventListener('unhandledrejection', event => {
    const message = event.reason?.message || String(event.reason || '');
    if (isChunkError(message)) {
      recover();
      return;
    }
    datadogRum.addError(toError(event.reason), {
      source: 'unhandledrejection',
    });
  });

  window.addEventListener('error', event => {
    if (isChunkError(event.message)) {
      recover();
      return;
    }
    datadogRum.addError(
      event.error ?? new Error(event.message || 'Unknown error'),
      {
        source: 'window.error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    );
  });
};
